import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import SocialIcons from "@/components/SocialIcons";
import { WhatsAppCTA } from "@/components/WhatsApp";
import { DEFAULT_WA_MESSAGES } from "@/config/site";
import { useSiteSettings } from "@/lib/siteSettings";

/** Shared header used across the public/landing pages. Shrinks on scroll and hosts theme + language + auth CTAs. */
export default function PublicHeader() {
  const { t } = useTranslation();
  const { nav } = useSiteSettings();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const NAV_DEFAULT = [
    { to: "/", key: "home", end: true },
    { to: "/team", key: "team" },
    { to: "/services", key: "services" },
    { to: "/prices", key: "prices" },
    { to: "/service-finder", key: "finder" },
    { to: "/resources", key: "resources" },
    { to: "/community", key: "community" },
    { to: "/testimonials", key: "testimonials" },
    { to: "/contact", key: "contact" },
  ];
  // Merge admin nav config (label/visibility/order) onto fixed routes — route
  // identity (`to`) is never editable, so renaming can't break navigation.
  const overrides = {};
  (nav?.items || []).forEach((it) => { if (it.route_key) overrides[it.route_key] = it; });
  const NAV = NAV_DEFAULT
    .map((l, i) => {
      const o = overrides[l.key] || {};
      return { ...l, labelOverride: o.label || "", visible: o.visible !== false, order: Number.isFinite(o.order) ? o.order : i };
    })
    .filter((l) => l.visible)
    .sort((a, b) => a.order - b.order);

  const signInLabel = nav?.signIn?.label || t("nav.signIn");
  const signInVisible = nav?.signIn?.visible !== false;
  const bookLabel = nav?.book?.label || t("nav.book");
  const bookPath = nav?.book?.path || "/contact";
  const bookVisible = nav?.book?.visible !== false;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed left-0 right-0 z-50 backdrop-blur-xl fl-nav ${scrolled ? "scrolled" : ""}`}
      style={{
        top: "var(--fl-banner-height, 0px)",
        borderBottom: "1px solid var(--fl-border)",
        background: scrolled ? "var(--fl-nav-scrolled)" : "var(--fl-bg)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center shrink-0" data-testid="brand-home">
          <Logo variant="wordmark" size="md" />
        </Link>

        <nav className="hidden md:flex items-center gap-5 lg:gap-6 text-sm" style={{ color: "var(--fl-text-2)" }}>
          {NAV.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `whitespace-nowrap transition-colors hover:text-[color:var(--fl-orange)] ${isActive ? "text-[color:var(--fl-orange)]" : ""}`
              }
              data-testid={`pub-nav-${l.key}`}
            >
              {l.labelOverride || t(`nav.${l.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <span className="hidden md:inline-flex items-center gap-3">
            {signInVisible && (
            <Link
              to="/login"
              className="fl-btn-anim relative px-4 py-2 rounded-full text-sm font-bold tracking-wider uppercase border-2 border-[color:var(--fl-orange)] text-[color:var(--fl-orange)] hover:bg-[color:var(--fl-orange)] hover:text-white transition-all whitespace-nowrap"
              data-testid="nav-login"
            >
              {signInLabel}
            </Link>
            )}
            {bookVisible && (
            <Link
              to={bookPath}
              className="fl-btn text-sm fl-cta-pulse whitespace-nowrap"
              data-testid="nav-book"
            >
              {bookLabel} <ArrowRight size={14} />
            </Link>
            )}
          </span>
          <button
            className="md:hidden"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            style={{ color: "var(--fl-text)" }}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="md:hidden border-t" style={{ borderColor: "var(--fl-border)", background: "var(--fl-bg)" }}>
          <div className="px-6 py-4 flex flex-col gap-4">
            {NAV.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `text-base transition-colors ${isActive ? "text-[color:var(--fl-orange)]" : ""}`}
                style={({ isActive }) => ({ color: isActive ? "var(--fl-orange)" : "var(--fl-text-2)" })}
              >
                {l.labelOverride || t(`nav.${l.key}`)}
              </NavLink>
            ))}
            <div className="pt-2">
              <WhatsAppCTA
                message={DEFAULT_WA_MESSAGES.contact}
                testid="mobile-nav-wa"
                variant="soft"
                size="sm"
                fullWidth
              >
                {t("pub.footer.waCta")}
              </WhatsAppCTA>
            </div>
            <div className="flex gap-3 pt-1 items-center">
              {signInVisible && <Link to="/login" onClick={() => setMobileOpen(false)} className="fl-btn-ghost text-sm flex-1 justify-center">{signInLabel}</Link>}
              {bookVisible && <Link to={bookPath} onClick={() => setMobileOpen(false)} className="fl-btn text-sm flex-1 justify-center">{bookLabel}</Link>}
            </div>
            <div className="pt-1 flex items-center justify-between">
              <SocialIcons size={20} showLabels testid="mobile-socials" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
